import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled, useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import moment from 'moment';
import { getApi, patchApi } from 'views/services/api';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Chip from '@mui/material/Chip';

import AddProperty from '../AddEmployee';
import EditProperty from '../EditEmployee';
import ChangePasswordForStaff from './ChangePassword';

import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeIcon from '@mui/icons-material/Home';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(3),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
}));

const InfoRow = ({ icon: Icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
      <Box sx={{ mr: 2, mt: 0.5, color: 'primary.main' }}>
        <Icon />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
          {value || 'N/A'}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [value, setValue] = React.useState('1');
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openPasswordChange, setPasswordChange] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenEditlead = () => setOpenEdit(true);
  const handleCloseEditlead = () => setOpenEdit(false);
  const handleOpenPasswordChange = () => setPasswordChange(true);
  const handleClosePasswordChange = () => setPasswordChange(false);

  const [employeeData, setEmployeeData] = useState([]);
  const [employeEmail, setEmployeeEmail] = useState('');
  const params = useParams();
  const employeeId = params.id;
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  const fetchEmployeeData = async () => {
    try {
      const response = await getApi(`api/employee/view/${employeeId}`);
      setEmployeeData(response?.data?.employeeData[0] || {});
      setEmployeeEmail(response?.data?.employeeData[0]?.email || '');
    } catch (error) {
      console.log(error);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await patchApi(`api/employee/toggle-status/${employeeId}`);
      fetchEmployeeData();
    } catch (error) {
      console.error('Error al cambiar estado del empleado:', error);
    }
  };

  useEffect(() => {
    fetchEmployeeData();
  }, [openAdd, openEdit]);

  return (
    <>
      <AddProperty open={openAdd} handleClose={handleCloseAdd} />
      <EditProperty open={openEdit} handleClose={handleCloseEditlead} employeeData={employeeData} />
      <ChangePasswordForStaff open={openPasswordChange} handleClose={handleClosePasswordChange} employeEmail={employeEmail} />

      <Box sx={{ width: '100%', typography: 'body1' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" fontWeight="600" color="primary.main">
              Detalles del Empleado
            </Typography>
            {employeeData.isActive !== false ? (
              <Chip label="Activo" color="success" size="small" sx={{ borderRadius: 1 }} />
            ) : (
              <Chip label="Inactivo" color="error" size="small" variant="outlined" sx={{ borderRadius: 1 }} />
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            onClick={() => navigate(-1)}
          >
            Volver
          </Button>
        </Box>

        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange} textColor="primary" indicatorColor="primary">
              <Tab label="Información General" value="1" sx={{ fontWeight: 600, textTransform: 'none' }} />
            </TabList>
          </Box>
          <TabPanel value="1" sx={{ px: 0, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Item>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                    Perfil del Empleado
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <InfoRow icon={PersonIcon} label="Nombres" value={employeeData.firstName} />
                    <InfoRow icon={PersonIcon} label="Apellidos" value={employeeData.lastName} />
                    <InfoRow icon={EmailIcon} label="Correo Electrónico" value={employeeData.email} />
                    <InfoRow icon={WorkIcon} label="Cargo / Rol" value={employeeData.employeeType} />
                    <InfoRow icon={AttachMoneyIcon} label="Salario" value={`${currency} ${employeeData.salary || '0'}`} />
                    <InfoRow icon={WorkIcon} label="Turno de Trabajo" value={employeeData.shift} />
                    <InfoRow icon={HomeIcon} label="Dirección de Residencia" value={employeeData.address} />
                    <InfoRow 
                      icon={DateRangeIcon} 
                      label="Fecha de Ingreso" 
                      value={employeeData.createdDate ? moment(employeeData.createdDate).format('DD/MM/YYYY') : 'N/A'} 
                    />
                    <InfoRow icon={BadgeIcon} label="Tipo de Documento" value={employeeData.idCardType} />
                    <InfoRow icon={BadgeIcon} label="Número de Documento" value={employeeData.idcardNumber} />
                  </Grid>

                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Documentos Adjuntos</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        {employeeData.idFile ? (
                          <Button
                            href={employeeData.idFile}
                            target="_blank"
                            rel="noreferrer"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            fullWidth
                            sx={{ justifyContent: 'flex-start', py: 1.5, borderRadius: 2, textTransform: 'none' }}
                          >
                            Ver Documento (Frente)
                          </Button>
                        ) : (
                          <Typography variant="body2" color="textSecondary">No hay documento frontal adjunto.</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {employeeData.idFile2 ? (
                          <Button
                            href={employeeData.idFile2}
                            target="_blank"
                            rel="noreferrer"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            fullWidth
                            sx={{ justifyContent: 'flex-start', py: 1.5, borderRadius: 2, textTransform: 'none' }}
                          >
                            Ver Documento (Reverso)
                          </Button>
                        ) : (
                          <Typography variant="body2" color="textSecondary">No hay documento reverso adjunto.</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Item>
              </Grid>
            </Grid>
          </TabPanel>
        </TabContext>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            color={employeeData.isActive !== false ? 'error' : 'success'}
            startIcon={employeeData.isActive !== false ? <BlockIcon /> : <CheckCircleOutlineIcon />}
            onClick={handleToggleStatus}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {employeeData.isActive !== false ? 'Desactivar Empleado' : 'Activar Empleado'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ChangeCircleIcon />}
            onClick={handleOpenPasswordChange}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cambiar Contraseña
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleOpenEditlead}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            Editar Empleado
          </Button>
        </Box>
      </Box>
    </>
  );
}
